// Meeting info modal (Calendar): opens when an organizer clicks an event. Shows
// the meeting's title, schedule, encryption + lock state and a copyable invite
// link, with a primary "Start meeting" action that jumps into the room.

import { useCallback, useState } from 'react';

import { MeetingSummary, meetingShareLink } from '../api';
import { pushToast } from '../store/callStore';
import { Button } from '../ui/Button';
import { IconCalendar, IconCheck, IconClock, IconCopy, IconLock, IconPlay } from '../ui/icons';
import { Modal } from '../ui/Modal';

export function MeetingInfoModal({
  meeting,
  onClose,
  onStart,
}: {
  meeting: MeetingSummary;
  onClose: () => void;
  onStart: (slug: string) => void;
}) {
  const [copied, setCopied] = useState(false);
  const start = useCallback(() => onStart(meeting.slug), [meeting.slug, onStart]);
  const link = meetingShareLink(`/m/${meeting.slug}`);
  const copyLink = useCallback(() => {
    navigator.clipboard
      ?.writeText(link)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1600);
      })
      .catch(() => pushToast('Could not copy the link', 'warning'));
  }, [link]);

  const when = meeting.scheduledAt ? new Date(meeting.scheduledAt) : null;
  const dateStr = when
    ? when.toLocaleDateString(undefined, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : 'Not scheduled — starts whenever you open it';
  const timeStr = when
    ? when.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    : null;
  const zk = meeting.e2eeModel === 'b';

  return (
    <Modal title={meeting.title ?? meeting.slug} onClose={onClose} width={440}>
      <div className="bz-mi">
        <div className="bz-mi-badges">
          <span className={zk ? 'bz-mi-badge bz-mi-badge-zk' : 'bz-mi-badge'}>
            {zk ? 'Zero-knowledge E2EE' : 'End-to-end encrypted'}
          </span>
          {meeting.locked && (
            <span className="bz-mi-badge bz-mi-badge-lock">
              <IconLock size={12} /> Locked
            </span>
          )}
        </div>

        <div className="bz-mi-meta">
          <div className="bz-mi-metarow">
            <IconCalendar size={16} />
            <span>{dateStr}</span>
          </div>
          {timeStr && (
            <div className="bz-mi-metarow">
              <IconClock size={16} />
              <span>{timeStr}</span>
            </div>
          )}
        </div>

        <div className="bz-mi-link">
          <span className="bz-mi-linktext">{link.replace(/^https?:\/\//, '')}</span>
          <button type="button" className="bz-mi-copy" onClick={copyLink}>
            {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>

        <div className="bz-mi-actions">
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button variant="primary" onClick={start}>
            <IconPlay size={15} /> Start meeting
          </Button>
        </div>
      </div>
    </Modal>
  );
}
